import type { ValidData as StopFormValidData } from '@/components/forms/JobEditStopForm.vue';

import { type Operation, compare } from 'fast-json-patch';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { useJobEditSectionBase } from '@/composables/jobEdit/useJobEditSectionBase';
import { useJobEditStore } from '@/stores/useJobEditStore';
import { fetchPutJobEditStopSection } from '@/helpers/fetchers';
import { KJobEditStopError } from '@/helpers/throwables';

export interface StopSectionInstance {
  validate: () => Promise<boolean>;
  getFormData: () => StopFormValidData | null;
}

export function useJobEditStops() {
  const jobEditStore = useJobEditStore();

  const stopSectionRefs = new Map<number, StopSectionInstance>();

  function registerStopRef(
    stopNumber: number,
    instance: StopSectionInstance | null,
  ): void {
    if (instance) {
      stopSectionRefs.set(stopNumber, instance);
    } else {
      stopSectionRefs.delete(stopNumber);
    }
  }

  function buildAllStopsPatch(
    formDataByStop: Map<number, StopFormValidData | null>,
    newStopNumbers: number[],
    deletedStopNumbers: number[],
  ): { patchOps: Operation[]; stopNumbers: number[] } {
    const originalJobData = jobEditStore.originalJobData;
    const currentJobData = jobEditStore.jobData;
    if (!originalJobData || !currentJobData) {
      return { patchOps: [], stopNumbers: [] };
    }

    const original = { stops: cloneDeep(originalJobData.stops) };
    const updated = { stops: cloneDeep(originalJobData.stops) };
    const stopNumbers: number[] = [];

    // Existing stops — apply form data changes
    for (const [stopNumber, formData] of formDataByStop.entries()) {
      if (!formData) continue;
      const idx = updated.stops.findIndex((s) => s.stop_number === stopNumber);
      if (idx === -1) continue;
      const before = original.stops[idx];
      Object.assign(updated.stops[idx], formData);
      const isChanged = !isEqual(before, updated.stops[idx]);
      if (isChanged) stopNumbers.push(stopNumber);
    }

    // New stops — build full stop object from current state + form data
    for (const stopNumber of newStopNumbers) {
      const currentStop = currentJobData.stops.find(
        (s) => s.stop_number === stopNumber,
      );
      if (!currentStop) continue;
      const newStop = cloneDeep(currentStop);
      const formData = formDataByStop.get(stopNumber);
      if (formData) Object.assign(newStop, formData);
      updated.stops.push(newStop);
      stopNumbers.push(stopNumber);
    }
    updated.stops.sort((a, b) => a.stop_number - b.stop_number);

    // Deleted stops
    for (const stopNumber of deletedStopNumbers) {
      const idx = updated.stops.findIndex((s) => s.stop_number === stopNumber);
      if (idx !== -1) {
        updated.stops.splice(idx, 1);
        stopNumbers.push(stopNumber);
      }
    }

    return { patchOps: compare(original, updated), stopNumbers };
  }

  async function saveOperation(): Promise<boolean> {
    // Snapshot before any async ops — v-for function refs are re-called on every
    // render, so the Map can be cleared+refilled between awaits.
    const entries = [...stopSectionRefs.entries()];

    if (entries.length === 0) return true;

    // Collect form data immediately (sync), before validation triggers re-renders.
    const formDataByStop = new Map(
      entries.map(([stopNumber, ref]) => [stopNumber, ref.getFormData()]),
    );

    const validations = await Promise.all(
      entries.map(([, ref]) => ref.validate()),
    );

    if (validations.some((v) => !v)) {
      return false;
    }

    const jobId = jobEditStore.jobId;
    const jobDate = jobEditStore.jobDate;

    if (!jobId || !jobDate) {
      throw new KJobEditStopError('Job ID or Job Date is not available');
    }

    const originalStopNumbers = new Set(
      jobEditStore.originalJobData?.stops.map((s) => s.stop_number) ?? [],
    );

    const newStopNumbers = [...formDataByStop.keys()].filter(
      (n) => !originalStopNumbers.has(n),
    );
    const deletedStopNumbers = [...originalStopNumbers].filter(
      (n) => !formDataByStop.has(n),
    );
    const { patchOps, stopNumbers } = buildAllStopsPatch(
      formDataByStop,
      newStopNumbers,
      deletedStopNumbers,
    );

    if (patchOps.length === 0) return true;

    await fetchPutJobEditStopSection(
      {
        job_id: jobId,
        job_date: jobDate,
        stop_numbers: stopNumbers.join(','),
      },
      patchOps,
    );

    return true;
  }

  const sectionBase = useJobEditSectionBase({
    sectionName: 'stops',
    saveOperation,
    onCancel: () => {
      jobEditStore.restoreStops();
    },
  });

  async function cancelAll(): Promise<void> {
    await sectionBase.cancelEditing();
  }

  return {
    isEditing: sectionBase.isEditing,
    isLoading: sectionBase.isLoading,
    startEditing: sectionBase.startEditing,
    cancelAll,
    saveAll: sectionBase.saveEditing,
    registerStopRef,
  };
}
